import { useState, useEffect, useMemo, useCallback, startTransition, MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PointOfSaleIcon,
  MarketsIcon,
  PersonAddIcon as CustomersIcon,
  ProductIcon,
  StoreIcon,
  SettingsIcon,
  OrderIcon,
  DataPresentationIcon as ReportIcon,
  HomeIcon,
  ChatIcon,
  StarIcon,
  PhoneIcon,
  ContentIcon,
  MegaphoneIcon,
  DiscountIcon,
  DeliveryIcon,
  AppsIcon,
  HomeFilledIcon,
  OrderFilledIcon,
  ProductFilledIcon,
  ChartVerticalFilledIcon as ReportFilledIcon,
  StoreFilledIcon,
  SettingsFilledIcon,
  PersonFilledIcon as CustomersFilledIcon,
} from '@/lib/polaris-icons';
import { Navigation, type IconSource } from '@/lib/polaris';
import styles from './Sidebar.module.css';
import { useAuth } from '@smarty/shared';
import { useFeatureFlags } from '@smarty/shared';
import { FeatureName } from '@/config/features.config';
import { ROUTES, storePath } from '@/config/routes.config';
import { useOpsActiveSection } from '@/store/useOpsActiveSection';
import { SIDEBAR_SUBNAV, NAV_BADGES, SIDEBAR_MAIN, type SidebarNavItem } from '@/config/navigation.config';
import { supabase } from '@/lib/supabase';
import { logError } from '@/lib/logger';
import { prefetchSection, prefetchLowPriorityAdminChunks } from '@/lib/prefetchRouteChunks';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchDashboardStats } from '@/lib/dashboardPrefetch';
import {
  prefetchCodAnalyticsStats,
  prefetchLoyaltyDashboardStats,
  prefetchStatisticsStats,
  prefetchCustomersStats,
  prefetchProductsStats,
  prefetchExistenciasStats,
  prefetchEcommerceOrdersStats,
  prefetchSalesOrdersStats,
  prefetchAbandonedCartsStats,
  prefetchShalomAgenciesStats,
  prefetchCategoriesStats,
  prefetchModifiersStats,
  prefetchTagsStats,
  prefetchVariantsStats,
  prefetchEcommercePaymentMethodsStats,
  prefetchEcommerceDeliveryMethodsStats,
  prefetchEcommerceDeliveryRulesStats,
  prefetchEcommercePagesStats,
  prefetchTenantDomainsStats,
  prefetchLogisticsCouriersCoverageStats,
} from '@/lib/prefetchStatsPages';
import { prefetchOnMainNavHover, scheduleIdleProductListPrefetch } from '@/lib/prefetchNavHover';
import { useWaPermissions } from '@/hooks/useWaPermissions';
import { useOpsRole } from '@/hooks/useOpsRole';
import { opsVisibleQueues, type OpsQueue } from '@/lib/operations/orderMath';

interface SubNavItem {
  url: string;
  label: string;
  badge?: string;
  feature?: FeatureName;
  activePaths?: string[];
}

interface SidebarProps {
  onDismiss?: () => void;
}

export function Sidebar({ onDismiss }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  // Sección de Operaciones según el pedido abierto (presupuesto/venta): el detalle vive en una
  // ruta compartida, así que resaltamos Presupuestos/Ventas por el stage, no por la URL.
  const opsActiveSection = useOpsActiveSection((s) => s.section);

  // Estados de expansión de secciones (derivadas de la ruta + forzadas al click)
  const [isOrdersExpanded, setIsOrdersExpanded] = useState(false);
  const [isProductsExpanded, setIsProductsExpanded] = useState(false);
  const [isReportsExpanded, setIsReportsExpanded] = useState(false);
  const [isOnlineStoreExpanded, setIsOnlineStoreExpanded] = useState(false);
  const [isPOSExpanded, setIsPOSExpanded] = useState(false);
  const [isLoyaltyExpanded, setIsLoyaltyExpanded] = useState(false);
  const [isCodExpanded, setIsCodExpanded] = useState(false);
  const [isCrmExpanded, setIsCrmExpanded] = useState(false);
  const [isCallCenterExpanded, setIsCallCenterExpanded] = useState(false);
  const [isTransporteExpanded, setIsTransporteExpanded] = useState(false);
  const [isWhatsAppExpanded, setIsWhatsAppExpanded] = useState(false);
  const [isOperacionesExpanded, setIsOperacionesExpanded] = useState(false);
  const [isChatbotExpanded, setIsChatbotExpanded] = useState(false);
  const [isAdsExpanded, setIsAdsExpanded] = useState(false);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);

  // Estados para badges de notificación
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({
    '/kds': 0,
    [ROUTES.ORDERS_ONLINE]: 0,
    [ROUTES.ORDERS_ABANDONED]: 0,
    [ROUTES.ORDERS]: 0,
  });
  
  const { isOwnerOrManager, loading: authLoading, tenantId, storeSlug } = useAuth();
  const qc = useQueryClient();
  const toUrl = useCallback((url: string) => (storeSlug ? storePath(storeSlug, url) : url), [storeSlug]);
  const { isFeatureEnabled, isDevMode } = useFeatureFlags();
  const waPerms = useWaPermissions();
  const { role: opsRole } = useOpsRole();

  // Cargar contadores de badges
  useEffect(() => {
    const loadBadgeCounts = async () => {
      if (authLoading || !tenantId) return;
      // No pollear si la pestaña no está visible (evita carga innecesaria).
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

      try {
        // Badges acotados a 100: el badge se muestra como "99+" arriba de 99, así
        // que contar TODO el set (count:'exact') es desperdicio y, a escala
        // (tenant grande × cada 30s × cada sesión), es carga constante en la DB.
        // Leemos ≤100 ids → costo O(100) por badge, no O(pedidos). Exacto 0-99.
        const kds = await supabase.from('orders_v2').select('id')
          .eq('tenant_id', tenantId).in('channel', ['pos', 'qr_table'])
          .in('lifecycle_stage', ['quotation', 'sale']).limit(100);

        const ecommerce = await supabase.from('orders_v2').select('id')
          .eq('tenant_id', tenantId).in('channel', ['online_checkout', 'cod_form'])
          .eq('lifecycle_stage', 'quotation').limit(100);

        const orders = await supabase.from('orders_v2').select('id')
          .eq('tenant_id', tenantId).in('lifecycle_stage', ['quotation', 'sale']).limit(100);

        let abandonedCartsCount = 0;
        try {
          const yesterday = new Date();
          yesterday.setHours(yesterday.getHours() - 24);
          const ab = await supabase.from('abandoned_carts').select('id')
            .eq('tenant_id', tenantId).eq('status', 'abandoned')
            .gte('created_at', yesterday.toISOString()).limit(100);
          abandonedCartsCount = ab.data?.length ?? 0;
        } catch {
          abandonedCartsCount = 0;
        }

        setBadgeCounts({
          '/kds': kds.data?.length ?? 0,
          '/orders/online': ecommerce.data?.length ?? 0,
          '/orders/abandoned': abandonedCartsCount,
          '/orders': orders.data?.length ?? 0,
        });
      } catch (error) {
        logError('Error cargando contadores de badges', error);
      }
    };

    loadBadgeCounts();
    const interval = setInterval(loadBadgeCounts, 30000);
    return () => clearInterval(interval);
  }, [authLoading, tenantId]);

  const formatBadge = useCallback((count: number): string | undefined => {
    if (count <= 0) return undefined;
    return count > 99 ? '99+' : count.toString();
  }, []);

  // Helper para verificar si una ruta está activa (usa URL con prefijo store cuando hay storeSlug)
  const isActive = useCallback((url: string): boolean => {
    const currentPath = location.pathname || '';
    const full = toUrl(url);
    return currentPath === full || (full !== '/' && currentPath.startsWith(full));
  }, [location.pathname, toUrl]);

  // Helper para crear sub-items con matches y filtrado
  const createSubItems = useCallback((items: SubNavItem[]) => {
    return items
      .filter((item) => {
        if (item.feature) {
          if (isDevMode) return true;
          return isFeatureEnabled(item.feature);
        }
        if (item.url === '/users' && !isOwnerOrManager()) {
          return false;
        }
        return true;
      })
      .map((item) => {
        const fullUrl = toUrl(item.url);
        const baseMatch = item.activePaths ? item.activePaths.some((p) => isActive(p)) : isActive(item.url);
        // Dentro del detalle de un pedido, resaltar Presupuestos/Ventas según su etapa (data-driven),
        // ya que la URL del pedido es compartida y no lo distingue.
        const matches =
          item.url === ROUTES.OPS_PEDIDOS ? baseMatch || opsActiveSection === 'pedidos'
            : item.url === ROUTES.OPS_VENTAS ? baseMatch || opsActiveSection === 'ventas'
              : baseMatch;
        return {
          url: fullUrl,
          label: item.label,
          badge: item.badge,
          matches,
          disabled: item.url === '/users' && !isOwnerOrManager(),
          onClick: (event?: MouseEvent) => {
            if (event) {
              event.preventDefault();
              event.stopPropagation();
            }
            startTransition(() => navigate(fullUrl));
          },
        };
      });
  }, [isActive, isFeatureEnabled, isDevMode, isOwnerOrManager, navigate, toUrl, opsActiveSection]);

  // Actualizar estado de expansión cuando cambia la ruta (usa URLs con prefijo store)
  useEffect(() => {
    const currentPath = location.pathname || '';
    setIsOrdersExpanded(
      currentPath.startsWith(toUrl(ROUTES.ORDERS)) ||
      currentPath.startsWith(toUrl(ROUTES.ORDERS_ONLINE)) ||
      currentPath.startsWith(toUrl(ROUTES.ORDERS_ABANDONED))
    );
    setIsProductsExpanded(currentPath.startsWith(toUrl(ROUTES.PRODUCTS_ROOT)));
    setIsReportsExpanded(
      currentPath.startsWith(toUrl(ROUTES.ANALYTICS)) ||
      currentPath.startsWith(toUrl(ROUTES.SALES_HEATMAP)) ||
      currentPath.startsWith(toUrl(ROUTES.SALES_RANKING))
    );
    setIsOnlineStoreExpanded(currentPath.startsWith(toUrl(ROUTES.ONLINE_STORE)));
    setIsPOSExpanded(
      currentPath.startsWith(toUrl(ROUTES.APPS_RESTAURANT)) ||
      currentPath === toUrl('/pos') ||
      currentPath.startsWith(toUrl('/tables'))
    );
    setIsLoyaltyExpanded(currentPath.startsWith(toUrl(ROUTES.APPS_LOYALTY)));
    setIsCodExpanded(currentPath.startsWith(toUrl(ROUTES.APPS_COD)));
    setIsCrmExpanded(currentPath.startsWith(toUrl(ROUTES.APPS_CRM_LEADS)) || currentPath.startsWith(toUrl(ROUTES.APPS_CRM_PIPELINE)) || currentPath.startsWith(toUrl(ROUTES.APPS_CRM_DIALER)) || currentPath.startsWith(toUrl(ROUTES.APPS_CRM_CALLS)));
    setIsOperacionesExpanded(currentPath.startsWith(toUrl('/apps/operaciones')));
    setIsChatbotExpanded(currentPath.startsWith(toUrl(ROUTES.APPS_CHATBOT)));
    setIsAdsExpanded(currentPath.startsWith(toUrl(ROUTES.ADS)));
    setIsSettingsExpanded(currentPath.startsWith(toUrl(ROUTES.SETTINGS)));
  }, [location.pathname, toUrl]);

  // Prefetch de datos del dashboard (Inicio): a 0.5s se precargan; al clicar Inicio suele ser al instante
  useEffect(() => {
    if (authLoading || !tenantId) return;
    const t = setTimeout(() => {
      prefetchDashboardStats(qc, tenantId).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [tenantId, authLoading, qc]);

  // Prefetch de Clientes, Productos e Inventario en segundo plano (1.5s) para que al abrirlos carguen al instante
  useEffect(() => {
    if (authLoading || !tenantId) return;
    const t = setTimeout(() => {
      prefetchCustomersStats(tenantId).catch(() => {});
      prefetchProductsStats(tenantId).catch(() => {});
      prefetchExistenciasStats(tenantId).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [tenantId, authLoading]);

  // Listas de productos (categorías, variantes, etc.): prefetch en idle para acortar la primera visita
  useEffect(() => {
    if (authLoading || !tenantId) return;
    const cancel = scheduleIdleProductListPrefetch(tenantId);
    return cancel;
  }, [tenantId, authLoading]);

  // Clientes, contenido, marketing, descuentos, WhatsApp: chunks lazy sin sección expand dedicada
  useEffect(() => {
    if (authLoading || !tenantId) return;
    const t = setTimeout(() => {
      prefetchLowPriorityAdminChunks();
    }, 2200);
    return () => clearTimeout(t);
  }, [tenantId, authLoading]);

  // Prefetch de chunks y de datos de estadísticas al expandir una sección
  useEffect(() => {
    if (isOrdersExpanded) {
      prefetchSection('orders');
      if (tenantId) {
        prefetchEcommerceOrdersStats(tenantId).catch(() => {});
        prefetchSalesOrdersStats(tenantId).catch(() => {});
        prefetchAbandonedCartsStats(tenantId).catch(() => {});
      }
    }
    if (isProductsExpanded) {
      prefetchSection('products');
      if (tenantId) {
        prefetchProductsStats(tenantId).catch(() => {});
        prefetchExistenciasStats(tenantId).catch(() => {});
      }
    }
    if (isReportsExpanded) {
      prefetchSection('reports');
      if (tenantId) prefetchStatisticsStats(tenantId).catch(() => {});
    }
    if (isOnlineStoreExpanded) {
      prefetchSection('onlineStore');
      if (tenantId) {
        prefetchAbandonedCartsStats(tenantId).catch(() => {});
        prefetchShalomAgenciesStats(tenantId).catch(() => {});
        prefetchEcommercePagesStats(tenantId).catch(() => {});
      }
    }
    if (isPOSExpanded) prefetchSection('pos');
    if (isLoyaltyExpanded) {
      prefetchSection('loyalty');
      if (tenantId) prefetchLoyaltyDashboardStats(tenantId).catch(() => {});
    }
    if (isCodExpanded) {
      prefetchSection('cod');
      if (tenantId) {
        prefetchCodAnalyticsStats(tenantId).catch(() => {});
      }
    }
    if (isCrmExpanded) {
      prefetchSection('crm');
      prefetchSection('callCenter');
    }
    if (isCallCenterExpanded) {
      prefetchSection('callCenter');
    }
    if (isTransporteExpanded) {
      prefetchSection('transporte');
    }
    if (isOperacionesExpanded) {
      prefetchSection('operaciones');
    }
    if (isChatbotExpanded) prefetchSection('chatbot');
    if (isSettingsExpanded) {
      prefetchSection('settings');
      if (tenantId) {
        prefetchEcommercePaymentMethodsStats(tenantId).catch(() => {});
        prefetchEcommerceDeliveryMethodsStats(tenantId).catch(() => {});
        prefetchEcommerceDeliveryRulesStats(tenantId).catch(() => {});
        prefetchTenantDomainsStats(tenantId).catch(() => {});
        prefetchLogisticsCouriersCoverageStats(tenantId).catch(() => {});
      }
    }
  }, [
    tenantId,
    isOrdersExpanded,
    isProductsExpanded,
    isReportsExpanded,
    isOnlineStoreExpanded,
    isPOSExpanded,
    isLoyaltyExpanded,
    isCodExpanded,
    isCrmExpanded,
    isCallCenterExpanded,
    isTransporteExpanded,
    isOperacionesExpanded,
    isChatbotExpanded,
    isSettingsExpanded,
  ]);

  // Catálogo de sub-items por sección. Mapea SIDEBAR_SUBNAV → SubNavItem[] y
  // aplica badges dinámicos (orders, pos) y filtros de permisos (whatsapp).
  const subnavByKey = useMemo(() => {
    const toSub = (i: { url: string; label: string; feature?: FeatureName; activePaths?: string[] }): SubNavItem => ({
      url: i.url,
      label: i.label,
      feature: i.feature,
      activePaths: i.activePaths,
    });

    const orders = SIDEBAR_SUBNAV.orders.map((item) => ({
      ...toSub(item),
      badge:
        item.url === ROUTES.ORDERS_ONLINE ? formatBadge(badgeCounts[ROUTES.ORDERS_ONLINE] || 0)
        : item.url === ROUTES.ORDERS_ABANDONED ? formatBadge(badgeCounts[ROUTES.ORDERS_ABANDONED] || 0)
        : undefined,
    }));

    const pos = SIDEBAR_SUBNAV.pos.map((item) => ({
      ...toSub(item),
      badge: item.url === ROUTES.APPS_RESTAURANT_KDS ? formatBadge(badgeCounts['/kds'] || 0) : undefined,
    }));

    const whatsappAll = SIDEBAR_SUBNAV.whatsapp?.map(toSub) ?? [];
    // Agentes solo ven Inbox; supervisores/admins ven todo.
    const whatsapp = (waPerms.isAgent && !waPerms.isSupervisor)
      ? whatsappAll.filter((i) => i.url === ROUTES.WHATSAPP_INBOX)
      : whatsappAll;

    return {
      orders,
      pos,
      whatsapp,
      home: SIDEBAR_SUBNAV.home.map(toSub),
      products: SIDEBAR_SUBNAV.products.map(toSub),
      reports: SIDEBAR_SUBNAV.reports.map(toSub),
      onlineStore: SIDEBAR_SUBNAV.onlineStore.map(toSub),
      loyalty: SIDEBAR_SUBNAV.loyalty?.map(toSub) ?? [],
      cod: SIDEBAR_SUBNAV.cod?.map(toSub) ?? [],
      contabilidad: SIDEBAR_SUBNAV.contabilidad?.map(toSub) ?? [],
      // Colas por rol: cada rol ve solo sus colas; lo legacy/extra solo el admin.
      operaciones: (() => {
        const allowed = opsVisibleQueues(opsRole);
        const queueByUrl: Record<string, OpsQueue> = {
          [ROUTES.OPS_PEDIDOS]: 'pedidos', [ROUTES.OPS_VENTAS]: 'ventas', [ROUTES.OPS_VENTAS_2]: 'ventas',
          [ROUTES.OPS_LOGISTICA]: 'logistica', [ROUTES.OPS_COBRANZAS]: 'cobranzas',
          [ROUTES.OPS_CANCELADAS]: 'canceladas',
        };
        return (SIDEBAR_SUBNAV.operaciones?.map(toSub) ?? []).filter((i) => {
          const q = queueByUrl[i.url];
          return q ? allowed.includes(q) : opsRole === 'admin';
        });
      })(),
      chatbot: SIDEBAR_SUBNAV.chatbot?.map(toSub) ?? [],
      crm: SIDEBAR_SUBNAV.crm?.map(toSub) ?? [],
      callCenter: SIDEBAR_SUBNAV.callCenter?.map(toSub) ?? [],
      transporte: SIDEBAR_SUBNAV.transporte?.map(toSub) ?? [],
      dropshipping: SIDEBAR_SUBNAV.dropshipping?.map(toSub) ?? [],
      agents: SIDEBAR_SUBNAV.agents?.map(toSub) ?? [],
      settings: SIDEBAR_SUBNAV.settings.map(toSub),
    } as const;
  }, [badgeCounts, formatBadge, waPerms.isAgent, waPerms.isSupervisor, opsRole]);

  // Helper para obtener el icono correcto (outline cuando está seleccionado, filled cuando no - como Shopify)
  const getIcon = useCallback((isSelected: boolean, outlineIcon: IconSource, filledIcon: IconSource) => {
    return isSelected ? outlineIcon : filledIcon;
  }, []);

  const getMainIcon = useCallback((key: string) => {
    switch (key) {
      case 'home':
        return getIcon(location.pathname === ROUTES.HOME, HomeIcon, HomeFilledIcon);
      case 'orders':
        return getIcon(isActive(ROUTES.ORDERS) || isActive(ROUTES.ORDERS_ONLINE) || isActive(ROUTES.ORDERS_ABANDONED), OrderIcon, OrderFilledIcon);
      case 'products':
        return getIcon(isActive(ROUTES.PRODUCTS_ROOT), ProductIcon, ProductFilledIcon);
      case 'customers':
        return getIcon(isActive(ROUTES.CUSTOMERS), CustomersIcon, CustomersFilledIcon);
      case 'analytics':
        return getIcon(isActive(ROUTES.ANALYTICS) || isActive(ROUTES.SALES_HEATMAP) || isActive(ROUTES.SALES_RANKING), ReportIcon, ReportFilledIcon);
      case 'onlineStore':
        return getIcon(isActive(ROUTES.ONLINE_STORE), StoreIcon, StoreFilledIcon);
      case 'pos':
        return PointOfSaleIcon;
      case 'marketplaces':
        return MarketsIcon;
      case 'loyalty':
        return StarIcon;
      case 'cod':
      case 'operaciones':
        return OrderIcon;
      case 'crm':
        return getIcon(isActive(ROUTES.APPS_CRM_LEADS) || isActive(ROUTES.APPS_CRM_PIPELINE), CustomersIcon, CustomersFilledIcon);
      case 'callCenter':
        return PhoneIcon;
      case 'transporte':
        return DeliveryIcon;
      case 'content':
        return ContentIcon;
      case 'marketing':
        return MegaphoneIcon;
      case 'ads':
        return MegaphoneIcon;
      case 'discounts':
        return DiscountIcon;
      case 'modules':
        return AppsIcon;
      case 'whatsapp':
      case 'chatbot':
        return ChatIcon;
      case 'settings':
        return getIcon(isSettingsExpanded, SettingsIcon, SettingsFilledIcon);
      default:
        return undefined;
    }
  }, [getIcon, isActive, isSettingsExpanded, location.pathname]);

  const getMainMatches = useCallback((key: string, url: string) => {
    switch (key) {
      case 'home':
        return location.pathname === ROUTES.HOME || location.pathname === '/';
      case 'orders':
        return isActive(ROUTES.ORDERS) || isActive(ROUTES.ORDERS_ONLINE) || isActive(ROUTES.ORDERS_ABANDONED);
      case 'products':
        return isActive(ROUTES.PRODUCTS_ROOT);
      case 'analytics':
        return isActive(ROUTES.ANALYTICS) || isActive(ROUTES.SALES_HEATMAP) || isActive(ROUTES.SALES_RANKING);
      case 'pos':
        return isActive(ROUTES.APPS_RESTAURANT) || isActive('/apps/restaurante') || isActive('/pos') || isActive('/tables');
      case 'loyalty':
        return isActive(ROUTES.APPS_LOYALTY);
      case 'cod':
        return location.pathname.startsWith(toUrl(ROUTES.APPS_COD));
      case 'crm':
        return location.pathname.startsWith(toUrl(ROUTES.APPS_CRM_LEADS))
          || location.pathname.startsWith(toUrl(ROUTES.APPS_CRM_PIPELINE));
      case 'callCenter':
        return location.pathname.startsWith(toUrl(ROUTES.APPS_CRM_DIALER))
          || location.pathname.startsWith(toUrl(ROUTES.APPS_CRM_CALLS))
          || location.pathname.startsWith(toUrl(ROUTES.APPS_CALL_CENTER));
      case 'whatsapp':
        return location.pathname.startsWith(toUrl(ROUTES.WHATSAPP_INBOX))
          || location.pathname.startsWith(toUrl(ROUTES.WHATSAPP_INBOXES))
          || location.pathname.startsWith(toUrl(ROUTES.WHATSAPP_SETTINGS)); // legacy redirect
      case 'operaciones':
        return location.pathname.startsWith(toUrl('/apps/operaciones'));
      case 'transporte':
        return (
          location.pathname.startsWith(toUrl(ROUTES.TRANSPORT_AGENCIES)) ||
          location.pathname.startsWith(toUrl(ROUTES.TRANSPORT_MOTORIZADOS))
        );
      case 'content':
        return location.pathname.startsWith(toUrl(ROUTES.CONTENT));
      case 'marketing':
        return location.pathname.startsWith(toUrl(ROUTES.MARKETING));
      case 'ads':
        return location.pathname.startsWith(toUrl(ROUTES.ADS));
      case 'discounts':
        return location.pathname.startsWith(toUrl(ROUTES.DISCOUNTS));
      case 'modules':
        return location.pathname.startsWith(toUrl(ROUTES.SETTINGS_APPS_AND_SALES_CHANNELS));
      case 'chatbot':
        return location.pathname.startsWith(toUrl(ROUTES.APPS_CHATBOT));
      case 'settings':
        return isSettingsExpanded;
      default:
        return isActive(url);
    }
  }, [isActive, isSettingsExpanded, location.pathname, toUrl]);

  const getMainBadge = useCallback((key: string, badgeKey?: keyof typeof NAV_BADGES) => {
    if (badgeKey) return NAV_BADGES[badgeKey];
    if (key === 'orders') return formatBadge(badgeCounts[ROUTES.ORDERS] || 0);
    return undefined;
  }, [badgeCounts, formatBadge]);

  // Siempre devolvemos los sub-items a Polaris. Polaris decide cuándo mostrarlos
  // con su lógica interna (selected || expanded || childIsActive). Para los pocos
  // items con toggle puro (onlineStore, loyalty) inyectamos `expanded` en el item
  // builder de abajo. Esto evita el bug histórico de double-gate donde el subnav
  // no aparecía al entrar al módulo por URL directa, Cmd+K o redirect.
  const getMainSubNav = useCallback((subnavKey?: keyof typeof subnavByKey) => {
    if (!subnavKey) return undefined;
    const items = subnavByKey[subnavKey];
    if (!items) return undefined;
    return createSubItems(items);
  }, [createSubItems, subnavByKey]);

  const handleMainClick = useCallback((key: string, url: string, event?: MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // startTransition evita flash: React mantiene la pantalla actual hasta que el nuevo contenido esté listo.
    const doNavigate = (targetUrl: string) => {
      startTransition(() => navigate(targetUrl));
    };

    // Mantener el mismo comportamiento actual por item.
    switch (key) {
      case 'orders':
        doNavigate(url);
        setIsOrdersExpanded(true);
        return;
      case 'products':
        doNavigate(url);
        setIsProductsExpanded(true);
        return;
      case 'analytics':
        doNavigate(url);
        setIsReportsExpanded(true);
        return;
      case 'onlineStore':
        setIsOnlineStoreExpanded(!isOnlineStoreExpanded);
        return;
      case 'pos':
        doNavigate(url);
        setIsPOSExpanded(true);
        return;
      case 'loyalty':
        setIsLoyaltyExpanded(!isLoyaltyExpanded);
        return;
      case 'crm':
        doNavigate(url);
        setIsCrmExpanded(true);
        return;
      case 'callCenter':
        doNavigate(url);
        setIsCallCenterExpanded(true);
        return;
      case 'transporte':
        doNavigate(url);
        setIsTransporteExpanded(true);
        return;
      case 'content':
      case 'marketing':
      case 'discounts':
      case 'modules':
        doNavigate(url);
        return;
      case 'whatsapp':
        doNavigate(url);
        setIsWhatsAppExpanded(true);
        return;
      case 'operaciones':
        doNavigate(url);
        setIsOperacionesExpanded(true);
        return;
      case 'chatbot':
        doNavigate(url);
        setIsChatbotExpanded(true);
        return;
      case 'ads':
        doNavigate(url);
        setIsAdsExpanded(true);
        return;
      case 'settings':
        doNavigate(toUrl(ROUTES.SETTINGS));
        setIsSettingsExpanded(true);
        return;
      default:
        doNavigate(url);
        return;
    }
  }, [isOnlineStoreExpanded, isLoyaltyExpanded, isSettingsExpanded, navigate, toUrl]);

  // Ítems principales con feature solo se muestran si el módulo está habilitado (o en dev)
  const filterSectionItems = useCallback(
    (items: readonly SidebarNavItem[]) =>
      items.filter((item) => {
        if (item.feature == null) return true;
        if (isDevMode) return true;
        return isFeatureEnabled(item.feature);
      }),
    [isDevMode, isFeatureEnabled]
  );

  const renderSection = (section: (typeof SIDEBAR_MAIN.sections)[number]) => (
    <Navigation.Section
      key={section.key}
      title={section.title}
      items={filterSectionItems(section.items).map((item) => {
        const icon = getMainIcon(item.key);
        const fullUrl = toUrl(item.url);
        const navHoverPrefetch =
          tenantId && !authLoading
            ? () => {
                if (item.key === 'products') prefetchOnMainNavHover('products', tenantId);
                else if (item.key === 'onlineStore') prefetchOnMainNavHover('onlineStore', tenantId);
                else if (item.key === 'settings') prefetchOnMainNavHover('settings', tenantId);
                else if (item.key === 'orders') prefetchOnMainNavHover('orders', tenantId);
                else if (item.key === 'crm') prefetchOnMainNavHover('crm', tenantId);
                // Operaciones es la ruta más lenta del panel según el RUM: precalentar su lista al
                // pasar el mouse es donde más se nota (ver `lib/prefetchNavHover.ts`).
                else if (item.key === 'operaciones') prefetchOnMainNavHover('operaciones', tenantId);
              }
            : undefined;
        // onlineStore y loyalty hacen toggle puro (no navegan al hacer click en
        // el L1). Como no quedan `selected` ni con `childIsActive`, Polaris no
        // los expandiría por sí solo: les inyectamos `expanded` explícito. El
        // resto de items confía en la detección automática de Polaris.
        const expandedOverride =
          item.key === 'onlineStore' ? isOnlineStoreExpanded
          : item.key === 'loyalty' ? isLoyaltyExpanded
          : undefined;
        return {
          url: fullUrl,
          label: item.label,
          icon: icon || HomeIcon,
          matches: getMainMatches(item.key, item.url),
          badge: getMainBadge(item.key, item.badge),
          onClick: (event?: MouseEvent) => handleMainClick(item.key, fullUrl, event),
          subNavigationItems: getMainSubNav(item.subnavKey),
          ...(expandedOverride !== undefined ? { expanded: expandedOverride } : {}),
          ...(navHoverPrefetch ? { onMouseEnter: navHoverPrefetch } : {}),
        };
      })}
    />
  );

  return (
    <div className={styles.root}>
      {/* Navegación principal — SCROLLEA cuando el menú es más alto que la pantalla. */}
      <Navigation location={location.pathname || '/'} onDismiss={onDismiss}>
        {SIDEBAR_MAIN.sections.filter((s) => s.key !== 'footer').map(renderSection)}
      </Navigation>
      {/* "Configuración" fija abajo, FUERA del área que scrollea: así nunca se sale de
          cuadro aunque el menú desborde (antes usaba position:sticky y se rompía). */}
      <div className={styles.footer}>
        <Navigation location={location.pathname || '/'} onDismiss={onDismiss}>
          {SIDEBAR_MAIN.sections.filter((s) => s.key === 'footer').map(renderSection)}
        </Navigation>
      </div>
    </div>
  );
}
